/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/
import React, { useState } from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { basicDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { Ioptions } from "../Filter/types";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../store/helpers/helperReducers";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { useSelector } from "react-redux";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../../ui/tooltip";
import { Button } from "../../ui/button";
import { Icon } from "../Icon";
import DeleteIcon from '@mui/icons-material/Delete';

interface IAssociationNavigationProps {
  navHeaderName: string;
  selectedItemIndex?: any;
  isManagePermission?: boolean;
  navItems: any[];
  ShowSearchFilterOnRecordsCount: number;
  addNavItem?: React.ElementType;
  confirmDeleteDialogOpener?: string;
  deleteConfirmationMessage: string;
  addDialogOpener?: string;
  handleNavItemClick?: (item: Ioptions, index: number) => void;
  deleteNavItemHandler?: (arg: any) => void;
  searchPlaceholder?: string;
  isCountBadgeVisible?: boolean;
  showCountForNone?: boolean;
  isDeleteIconVisible?: boolean;
  hideDeleteForValues?: (string | number)[];
  maxHeight?: string;
};

const AssociationNavigation: React.FC<IAssociationNavigationProps> = ({
  navHeaderName = "",
  selectedItemIndex,
  isManagePermission = false,
  navItems = [],
  ShowSearchFilterOnRecordsCount = 10,
  addNavItem: DynamicComponent,
  confirmDeleteDialogOpener,
  deleteConfirmationMessage = "",
  addDialogOpener,
  handleNavItemClick,
  deleteNavItemHandler,
  searchPlaceholder = "",
  isCountBadgeVisible = true,
  showCountForNone = false,
  isDeleteIconVisible = true,
  hideDeleteForValues = [],
  maxHeight = "calc(100vh - 12rem)",
}) => {
  const truncateText = (text: string, maxLength = 20) =>
    text && text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  const [isHovered, setIsHovered] = useState<Record<string, boolean>>({});
  const dispatch = useStoreDispatch();

  const basicDialodOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const [searchText, setSearchText] = useState("");
  const search = searchText?.toLowerCase();

  const filteredLabels = navItems.filter(
    (item: { text: string }) =>
      item.text?.toLowerCase().startsWith(search)
  );

  const [selectedItem, setSelectedItem] = useState<any | undefined>(selectedItemIndex);

  const deleteItemHandler = () => {
    dispatch(setConfirmDialog(confirmDeleteDialogOpener));
  };

  const handleOnClick = () => {
    dispatch(setBasicDialogOpenFor(addDialogOpener));
  };

  const handleRemoveAction = (actionName: string) => {
    if (actionName.toUpperCase() === constStr.yesDelete.toUpperCase()) {
      deleteNavItemHandler?.(selectedItem);
    }
  };

  return (
    <>
      {openConfirmDialog === confirmDeleteDialogOpener && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={(actionName) => handleRemoveAction(actionName)}>
          {deleteConfirmationMessage} <strong> {selectedItem?.text} </strong> ?
        </ConfirmDialog>
      )}

      {basicDialodOpenFor === addDialogOpener && DynamicComponent && (
        <DynamicComponent />
      )}

      <div className="space-y-6 ml-3 mt-2.5">
        <aside
        style={{
          backgroundColor: "var(--left-Navigation-background)",
          maxHeight,
        }}
          className="w-52 shrink-0 rounded-lg border border-border/80 overflow-hidden flex flex-col self-start">
          {/* Header */}
          <div className="p-3 pt-0 border-b border-border/80 bg-muted/20">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {navHeaderName}
              </h3>
              {isManagePermission && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 shrink-0 rounded-md border-0 p-0 hover:bg-accent bg-transparent"
                        aria-label={`Add ${navHeaderName}`}
                        onClick={handleOnClick}
                      >
                        <Icon name="add" size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Add {navHeaderName}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Search */}
            {ShowSearchFilterOnRecordsCount != undefined && navItems.length >= ShowSearchFilterOnRecordsCount && (
              <div className="relative mt-0.5">
                <Icon name="search" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  placeholder={searchPlaceholder || `Search ${navHeaderName.toLowerCase()}`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="h-8 pl-8 pr-2 w-full text-sm rounded-md bg-background text-foreground border border-border focus:border-ring focus:outline-none"
                />
              </div>
            )}
          </div>

          {/* Nav list */}
          <nav className="m-2 flex-1 min-h-0 overflow-y-auto">
            {filteredLabels.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No items match</p>
            ) : (
              filteredLabels.map((item: Ioptions, index: number) => {
                const isSelected = selectedItemIndex?.value === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => handleNavItemClick?.(item, index)}
                    onMouseEnter={() => setIsHovered((prev) => ({ ...prev, [item.value]: true }))}
                    onMouseLeave={() => setIsHovered((prev) => ({ ...prev, [item.value]: false }))}
                    className={`w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2.5 py-2 text-left text-sm transition-colors border-0 rounded-md ${
                      isSelected
                        ? 'bg-[var(--left-Navigation-selected-background)] text-accent-foreground font-medium'
                        : 'bg-transparent hover:bg-[var(--left-Navigation-hover-background)] text-foreground'
                    }`}
                  >
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="min-w-0 flex-1 truncate text-foreground">{truncateText(item.text)}</span>
                        </TooltipTrigger>
                        <TooltipContent>{item.text}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>


                    <span className="flex items-center gap-1 shrink-0">
                      {/* Count badge */}
                      {isCountBadgeVisible && (item.value !== "NONE" || showCountForNone) && (
                        <span
                          className={`tabular-nums text-xs px-2 py-0.5 rounded-full ${
                            isSelected 
                              ? 'bg-border font-medium shadow-sm'
                              : 'bg-muted text-muted-foreground dark:text-muted-foreground'
                          }`}
                        >
                          {(item as any).deviceCount ?? (item as any).count ?? 0}
                        </span>
                      )}

                      {/* Delete icon on hover */}
                      {isDeleteIconVisible && isManagePermission && item.value !== "NONE" && !hideDeleteForValues.includes(item.text) ? (
                        <button
                          type="button"
                          aria-label="Delete"
                          className={`p-0.5 rounded bg-background border-0 hover:bg-accent transition-opacity inline-flex items-center justify-center ${isHovered[item.value] ? "opacity-100" : "opacity-0"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                            deleteItemHandler();
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} className="text-destructive" />
                        </button>
                      ) : (
                        <span className="w-4" />
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </nav>
        </aside>
      </div>
    </>
  );
};

export default AssociationNavigation;